import { Stack } from '@mui/material';
import { AlertBanner } from '../../../../primitives/alert/alert-banner';
import { Button } from '../../../../primitives/button';
import { Typography } from '../../../../primitives/typography/typography';
import { useHoldLoginColumn } from '../login-column/use-hold-login-column';
import { TEXT_ACTION } from './text-action';
import { TwoStepSetup } from './two-step-setup-step';
import { TwoStepVerify } from './two-step-verify-step';
import {
  TWO_STEP_FIELD_REFUSALS,
  type TwoStepActions,
  type TwoStepFeedback,
  type TwoStepStep,
} from './two-step.types';

export type TwoStepStepsProps = TwoStepFeedback &
  TwoStepActions & {
    step: TwoStepStep;
    onCancel?: () => void;
  };

// A phone refusal belongs on the setup step's number field; a resend on the verify step has none.
const isOnField = (step: TwoStepStep, code = '') =>
  code === TWO_STEP_FIELD_REFUSALS.code ||
  (step.name === 'setup' &&
    (code === TWO_STEP_FIELD_REFUSALS.phoneNumber ||
      code === TWO_STEP_FIELD_REFUSALS.phoneCountry));

const headingFor = (step: TwoStepStep) => {
  if (step.name === 'setup' && step.changingNumber) return 'Change Your Mobile Number';
  if (step.name === 'setup') return 'Set Up Two-Step Sign-In';
  return 'Two-Step Sign-In';
};

const StepBody = (props: TwoStepStepsProps) => {
  const { step } = props;
  if (step.name === 'setup') return <TwoStepSetup {...props} step={step} />;
  return <TwoStepVerify {...props} step={step} />;
};

/** The setup and verify screens of two-step sign-in, rendered from `useTwoStepSignIn`. */
export const TwoStepSteps = (props: TwoStepStepsProps) => {
  const { step, error, notice, busy, onCancel } = props;
  const bannerError = error && !isOnField(step, error.code) ? error : null;
  useHoldLoginColumn(!!bannerError || !!notice);

  return (
    <Stack sx={{ width: '100%', gap: 1, '& > .MuiButton-text': TEXT_ACTION }}>
      <Typography.Heading>{headingFor(step)}</Typography.Heading>
      {step.email && <Typography.Subtitle>{step.email}</Typography.Subtitle>}
      {bannerError && <AlertBanner severity="error">{bannerError.message}</AlertBanner>}
      {notice && <AlertBanner severity="success">{notice}</AlertBanner>}
      <StepBody {...props} />
      {onCancel && (
        <Button variant="text" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      )}
    </Stack>
  );
};
